import { useState, useEffect } from 'react'
import Modal from '../../../components/Modal'
import Loader from '../../../components/Loader'
import BuildingIcon from '../../../icons/BuildingIcon'
import Button from '../../../components/Button'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { setCompanyOffer } from '../../../services/Admin'
import { TAGS } from '../../../services/Tags'
import Toast from '../../../components/Toast/Toast'
import { useTranslation } from 'react-i18next'
import { getListOfCompanies } from '../../../services/HiringAdvisor'
import Pagination from '../../../components/Pagination'
import { ITEMS_PER_PAGE } from '../../../utils/constants'

const AssignCompanyModal = ({ offerId, modalId, onClose }) => {
  const { t } = useTranslation()
  const [searchText, setSearchText] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedCompanyId, setSelectedCompanyId] = useState(null)
  const [page, setPage] = useState(0)
  const queryClient = useQueryClient()

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText)
      setPage(0)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchText])

  const { data: companies = {}, isLoading: isLoadingCompanies } = useQuery(
    [TAGS.list_companies_for_assign, debouncedSearch, page],
    () => getListOfCompanies({ page, searchText: debouncedSearch }),
    {
      keepPreviousData: true,
    }
  )

  const { mutate: assignCompany, isLoading: isLoadingAssign } = useMutation(
    [TAGS.set_company_offer],
    setCompanyOffer,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(TAGS.list_of_offers)
        queryClient.invalidateQueries(TAGS.offers_by_ha)
        Toast({
          title:
            t('offer_assigned_successfully') || 'Oferta asignada correctamente',
        })
        onClose()
      },
    }
  )

  const handleAssign = () => {
    if (selectedCompanyId) {
      assignCompany({
        offerId,
        companyId: selectedCompanyId,
      })
    }
  }

  const listCompanies = companies?.listCompanies || []
  const totalCompanies = companies?.countListCompanies || 0

  return (
    <Modal
      modalId={modalId}
      title={t('assign_offer_to_company') || 'Asignar oferta a empresa'}
      modalSize="modal-md"
    >
      <div className="d-flex flex-column">
        <span className="text-on-bg-secondary font-small mb-3">
          {t('select_company_to_assign') ||
            'Selecciona la empresa a la que deseas asignar esta oferta'}
        </span>

        <input
          type="text"
          className="form-control mb-3"
          placeholder={t('search_company') || 'Buscar empresa...'}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />

        <div className="overflow-auto" style={{ maxHeight: '250px' }}>
          {isLoadingCompanies ? (
            <div className="d-flex justify-content-center p-3">
              <Loader />
            </div>
          ) : listCompanies?.length === 0 ? (
            <div className="text-center text-on-bg-secondary p-3">
              {t('no_companies_found') || 'No se encontraron empresas'}
            </div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {listCompanies?.map((company) => (
                <div
                  key={company.id}
                  className={`d-flex align-items-center gap-3 p-2 rounded cursor-pointer ${
                    selectedCompanyId === company.id
                      ? 'bg-bg-CTA-primary-selected'
                      : 'hover-job-offer-card'
                  }`}
                  style={{ border: '1px solid #e0e0e0' }}
                  onClick={() => setSelectedCompanyId(company.id)}
                >
                  {company.logo ? (
                    <img
                      src={company.logo}
                      width={40}
                      height={40}
                      className="rounded-circle"
                      style={{ objectFit: 'cover' }}
                      alt={company.name}
                    />
                  ) : (
                    <div
                      className="rounded-circle bg-bg-tertiary d-flex justify-content-center align-items-center"
                      style={{ width: 40, height: 40 }}
                    >
                      <BuildingIcon width={20} height={20} />
                    </div>
                  )}
                  <div className="d-flex flex-column text-start">
                    <span className="fw-bold text-on-bg-primary font-small">
                      {company?.user?.username}
                    </span>
                    <span className="text-on-bg-secondary font-small-2">
                      {company?.user?.email}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-3">
          <Pagination
            page={page}
            setPage={setPage}
            itemsPerPage={ITEMS_PER_PAGE}
            totalItems={totalCompanies}
          />
        </div>

        <div className="d-flex justify-content-end align-items-center gap-3 mt-4">
          <Button
            type="button"
            className="btn-white text-on-bg-primary border border-on-bg-primary"
            disabled={isLoadingAssign}
            data-bs-dismiss="modal"
          >
            {t('cancel') || 'Cancelar'}
          </Button>
          <Button
            type="button"
            className="btn-bg-CTA-primary text-on-bg-primary"
            disabled={isLoadingAssign || !selectedCompanyId}
            onClick={handleAssign}
          >
            {isLoadingAssign ? <Loader /> : t('assign') || 'Asignar'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default AssignCompanyModal
